import { cn } from "@/lib/utils";
import { ChatStompProvider } from "@/contexts/chat-stomp-context";
import { FbConversation, FBMessage } from "@/types/fb-type";
import { Loader2, MessageCircle, Minus, Square, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import ChatWindow from "./ChatWindow";

type SizePreset = "sm" | "md" | "lg";

type ChatFloatingWidgetProps = {
  currentConversation: FbConversation | null;
  messages: FBMessage[];
  isLoadingMessage: boolean; // your existing loading flag
  prevCursor: string;
  OnNewMessage?: (m: FBMessage) => void;
  OnLoadMoreMessage?: (after: string) => void;

  /** NEW: control open state */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  /** UI options */
  initialSize?: SizePreset;
  title?: string;
  inactive?: boolean;
};

export default function ChatFloatingWidget({
  currentConversation,
  messages,
  isLoadingMessage,
  prevCursor,
  OnNewMessage,
  OnLoadMoreMessage,
  open: openProp,
  onOpenChange,
  initialSize = "lg",
  title = "Chat",
  inactive = false,
}: ChatFloatingWidgetProps) {
  const isControlled = typeof openProp === "boolean";
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? (openProp as boolean) : internalOpen;
  const [size, setSize] = useState<SizePreset>(initialSize);
  const [unread, setUnread] = useState(0);
  const lastCountRef = useRef(messages.length);
  const handleOpenChange = (next: boolean) => {
    if (!isControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  };

  // bump unread when closed and new message arrives
  useEffect(() => {
    const curr = messages.length;
    const prev = lastCountRef.current;
    if (!open && curr > prev) {
      setUnread((u) => u + (curr - prev));
    }
    lastCountRef.current = curr;
  }, [messages.length, open]);

  // reset unread when opened
  useEffect(() => {
    if (open && unread > 0) setUnread(0);
  }, [open, unread]);

  // Derived "opening" state: panel is open, first page still loading, no messages yet
  const isOpening = open && isLoadingMessage && messages.length === 0;

  const dims = useMemo(() => {
    switch (size) {
      case "sm":
        return "w-[22rem] h-[24rem]";
      case "md":
        return "w-[26rem] h-[30rem]";
      case "lg":
        return "w-[32rem] h-[38rem]";
      default:
        return "w-[26rem] h-[30rem]";
    }
  }, [size]);

  const getUser = () => {
    try {
      return currentConversation ? currentConversation.getUser() : null;
    } catch (err) {
      console.log('err get user', err);
      return null;
    }
  }

  const avatarUrl = getUser()?.profileImageUrl;
  const initials = useMemo(() => {
    const n = getUser()?.name?.trim() || "";
    const a = n?.split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "U";
    return a;
  }, [getUser]);

  return (
    <>
      {/* Floating toggle (closed) with loading indicator */}
      {!open && (
        <div className="fixed bottom-4 right-4 z-50 flex justify-end">
          <button
            onClick={() => handleOpenChange(true)}
            className={cn(
              "group relative flex items-center gap-2 rounded-full bg-blue-600 text-white px-4 py-3 shadow-lg",
              "hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all",
              "disabled:opacity-60 disabled:cursor-not-allowed"
            )}
            aria-label={isLoadingMessage ? "Loading chat…" : "Open chat"}
            aria-busy={isLoadingMessage ? true : undefined}
            disabled={isLoadingMessage} // prevent opening empty pane while fetching
          >
            <div className="relative">
              {isLoadingMessage ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <MessageCircle className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute -top-2 -right-3 inline-flex items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold px-1.5 py-0.5 min-w-[18px]">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </>
              )}
            </div>
            <span className="hidden sm:block text-sm font-medium">
              {isLoadingMessage ? "Loading…" : inactive ? "Chat history" : "Chat"}
            </span>
          </button>
        </div>
      )}

      {/* Popup Panel */}
      {open && (
        <div
          className={cn(
            "fixed bottom-4 right-4 z-50 rounded-xl border border-gray-200 bg-white shadow-2xl flex flex-col overflow-hidden",
            "backdrop-blur supports-[backdrop-filter]:bg-white/90",
            "max-h-[80vh] max-w-[95vw] sm:max-w-none",
            dims
          )}
          role="dialog"
          aria-label="Chat window"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-3 py-2 bg-white/80 sticky top-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center overflow-hidden">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="avatar"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-xs font-semibold">{initials}</span>
                )}
                {currentConversation?.isOnline() && (
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 ring-2 ring-white" />
                )}

              </div>
              <div className="truncate">
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {title}
                  {currentConversation?.getUser()?.name && (
                    <span className="text-gray-500 font-normal">
                      {" · "}
                      {currentConversation?.getUser()?.name}
                    </span>
                  )}
                </div>
                <div
                  className={cn(
                    "text-[11px]",
                    isOpening
                      ? "text-blue-600"
                      : currentConversation?.isOnline()
                        ? "text-emerald-600"
                        : "text-gray-500"
                  )}
                >
                  {isOpening
                    ? "opening…"
                    : inactive
                      ? "inactive · cached history"
                    : currentConversation?.isOnline()
                      ? "online"
                      : "offline"}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <select
                aria-label="Chat size"
                className="text-[12px] border rounded-md px-1.5 py-1 bg-white hover:bg-gray-50"
                value={size}
                onChange={(e) => setSize(e.target.value as SizePreset)}
              >
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
              </select>

              <button
                className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50"
                aria-label="Minimize"
                onClick={() => handleOpenChange(false)}
                disabled={isOpening}
                title={isOpening ? "Please wait…" : "Minimize"}
              >
                <Minus className="h-4 w-4 text-gray-700" />
              </button>

              <button
                className="p-2 rounded-md hover:bg-gray-100"
                aria-label="Open full screen"
                onClick={() => setSize("lg")}
              >
                <Square className="h-4 w-4 text-gray-700" />
              </button>

              <button
                className="p-2 rounded-md hover:bg-gray-100"
                aria-label="Close chat"
                onClick={() => handleOpenChange(false)}
              >
                <X className="h-4 w-4 text-gray-700" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0">
            {isOpening ? (
              // Opening skeleton (first load)
              <div className="h-full p-3 animate-pulse space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-slate-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-1/3 rounded bg-slate-200" />
                      <div className="h-3 w-2/3 rounded bg-slate-200" />
                      <div className="h-3 w-1/2 rounded bg-slate-200" />
                    </div>
                  </div>
                ))}
              </div>
            ) : currentConversation ? (
              <ChatStompProvider>
                <ChatWindow
                  currentConversation={currentConversation}
                  messages={messages}
                  isLoadingMessage={isLoadingMessage}
                  prevCursor={prevCursor}
                  OnNewMessage={OnNewMessage}
                  OnLoadMoreMessage={OnLoadMoreMessage}
                  hideHeader={true}
                  inactive={inactive}
                />
              </ChatStompProvider>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <p className="text-sm">No conversation selected</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer mini loader when more pages load */}
          {open && !isOpening && isLoadingMessage && (
            <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-500 border-t">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading messages…
            </div>
          )}
        </div>
      )}
    </>
  );
}
